import { ICON_PROPS, STATE_META, formatRelative, friendlyAiError, openEmailInOutlook, postToHost, shortDate, tierColor } from './shared.js';
import { useEffect, useState } from 'react';
import {
  Accordion,
  ActionIcon,
  Alert,
  AppShell,
  Badge,
  Box,
  Button,
  Card,
  Center,
  Checkbox,
  Chip,
  Code,
  Collapse,
  Container,
  Divider,
  Drawer,
  Grid,
  Group,
  List,
  Loader,
  Menu,
  Modal,
  MultiSelect,
  NativeSelect,
  NavLink,
  NumberInput,
  Paper,
  PasswordInput,
  Progress,
  RingProgress,
  ScrollArea,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Tabs,
  Text,
  TextInput,
  Textarea,
  ThemeIcon,
  Title,
  Tooltip,
  useMantineColorScheme,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconArchive,
  IconArrowBackUp,
  IconBell,
  IconBolt,
  IconBrain,
  IconBriefcase,
  IconCalendar,
  IconChartBar,
  IconCheck,
  IconChevronDown,
  IconChevronUp,
  IconClock,
  IconCommand,
  IconDeviceMobile,
  IconDownload,
  IconEdit,
  IconFileText,
  IconFlag,
  IconFolder,
  IconGavel,
  IconHistory,
  IconMail,
  IconMailOff,
  IconMessageQuestion,
  IconMoonStars,
  IconNews,
  IconPaperclip,
  IconPencil,
  IconPhone,
  IconPlus,
  IconRadar,
  IconReload,
  IconReport,
  IconSchool,
  IconSearch,
  IconSend,
  IconSettings,
  IconShare,
  IconShield,
  IconSparkles,
  IconStar,
  IconStarFilled,
  IconTarget,
  IconTrash,
  IconUser,
  IconUserPlus,
  IconUsersGroup,
  IconWand,
  IconX,
} from '@tabler/icons-react';
import { api } from '../../api.js';
// Round 48 follow-up (2026-07-15): AddinPage.tsx monolith split — morning brief family + insights + score,
// moved VERBATIM. Layering: shared ← shared-components ← panels ← AddinPage.

export function MorningBriefBanner(): React.JSX.Element | null {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.addinMorning>> | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .addinMorning()
      .then((r) => { if (!cancelled) setData(r); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!data) return null;
  // Show whenever there's something worth surfacing — across timezones, not
  // everyone works 9-to-5. Closeout card sits alongside it; both can show.
  const interesting =
    data.overnight_count > 5 ||
    data.new_p0.length > 0 ||
    data.new_security_hits.length > 0 ||
    data.overdue_promises > 0 ||
    data.sla_violations > 0;
  if (!interesting) return null;
  const hour = new Date().getHours();
  const greeting = hour < 5 ? "You're up late." : hour < 12 ? 'Good morning.' : hour < 18 ? 'Catching up?' : 'Wrapping the day?';

  return (
    <Card padding="md" radius="md" withBorder
      style={{ borderLeft: '4px solid var(--mantine-color-indigo-6)' }}>
      <Stack gap="sm">
        <Group gap="xs">
          <ThemeIcon variant="light" color="indigo">
            <IconSparkles {...ICON_PROPS} />
          </ThemeIcon>
          <Text fw={600}>{greeting} Here's what's changed since you last looked.</Text>
        </Group>
        <SimpleGrid cols={{ base: 2, sm: 5 }}>
          <Stack gap={0} align="center">
            <Text fw={700} size="lg">{data.overnight_count}</Text>
            <Text size="xs" c="dimmed">new emails</Text>
          </Stack>
          <Stack gap={0} align="center">
            <Text fw={700} size="lg" c={data.new_p0.length > 0 ? 'red' : 'dimmed'}>{data.new_p0.length}</Text>
            <Text size="xs" c="dimmed">new P0</Text>
          </Stack>
          <Stack gap={0} align="center">
            <Text fw={700} size="lg" c={data.new_security_hits.length > 0 ? 'red' : 'dimmed'}>{data.new_security_hits.length}</Text>
            <Text size="xs" c="dimmed">security hits</Text>
          </Stack>
          <Stack gap={0} align="center">
            <Text fw={700} size="lg" c={data.overdue_promises > 0 ? 'orange' : 'dimmed'}>{data.overdue_promises}</Text>
            <Text size="xs" c="dimmed">overdue promises</Text>
          </Stack>
          <Stack gap={0} align="center">
            <Text fw={700} size="lg" c={data.sla_violations > 0 ? 'red' : 'dimmed'}>{data.sla_violations}</Text>
            <Text size="xs" c="dimmed">SLA breaches</Text>
          </Stack>
        </SimpleGrid>
      </Stack>
    </Card>
  );
}

// =========================================================================
// Closeout card — daily rhythm. Shows what's still on your plate today,
// overdue promises, and snoozed items returning soon. Round 39 point 26.
// =========================================================================

export function CloseoutCard(): React.JSX.Element | null {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.addinCloseout>> | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .addinCloseout()
      .then((r) => {
        if (!cancelled) setData(r);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  if (!data) return null;
  const total = data.waiting_on_you_today + data.overdue_promises + data.snoozed_returning_soon;
  if (total === 0) return null;

  return (
    <Card
      padding="md"
      radius="md"
      withBorder
      style={{ borderLeft: '4px solid var(--mantine-color-blue-6)' }}
    >
      <Group gap="xl" wrap="wrap">
        <Group gap="xs">
          <ThemeIcon size="md" variant="light" color="blue">
            <IconClock {...ICON_PROPS} />
          </ThemeIcon>
          <Stack gap={0}>
            <Text fw={600} size="sm">
              Daily rhythm
            </Text>
            <Text size="xs" c="dimmed">
              {new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })}
            </Text>
          </Stack>
        </Group>
        <Group gap="lg">
          <CloseoutMetric
            label="Replies owed today"
            value={data.waiting_on_you_today}
            color={data.waiting_on_you_today > 0 ? 'orange' : 'gray'}
          />
          <CloseoutMetric
            label="Overdue promises"
            value={data.overdue_promises}
            color={data.overdue_promises > 0 ? 'red' : 'gray'}
          />
          <CloseoutMetric
            label="Snoozed returning"
            value={data.snoozed_returning_soon}
            color={data.snoozed_returning_soon > 0 ? 'grape' : 'gray'}
          />
        </Group>
      </Group>
    </Card>
  );
}

export function CloseoutMetric({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: string;
}): React.JSX.Element {
  return (
    <Stack gap={0} align="center">
      <Text fw={700} size="lg" c={color === 'gray' ? 'dimmed' : color}>
        {value}
      </Text>
      <Text size="xs" c="dimmed">
        {label}
      </Text>
    </Stack>
  );
}

// =========================================================================
// Promises section — commitments you've made in sent mail. Round 39 point 1.
// LLM scans recent sent mail; UI lets you mark fulfilled / cancelled.
// =========================================================================

export function PromisesSection(): React.JSX.Element | null {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.addinPromisesList>> | null>(null);
  const [scanning, setScanning] = useState(false);
  const [busy, setBusy] = useState(false);
  // Sort + filter (client-side; promises list is bounded and already loaded).
  // All persisted to localStorage so a refresh keeps the same view.
  const [sortBy, setSortBy] = useState<'due' | 'detected' | 'audience'>(() => {
    try { return (localStorage.getItem('addin_promises_sort') as 'due' | 'detected' | 'audience') ?? 'due'; }
    catch { return 'due'; }
  });
  const [bucket, setBucket] = useState<'all' | 'overdue' | 'today' | 'week' | 'future' | 'no_due'>(() => {
    try { return (localStorage.getItem('addin_promises_bucket') as 'all' | 'overdue' | 'today' | 'week' | 'future' | 'no_due') ?? 'all'; }
    catch { return 'all'; }
  });
  const [audienceFilter, setAudienceFilter] = useState<string | null>(() => {
    try { return localStorage.getItem('addin_promises_audience'); } catch { return null; }
  });
  useEffect(() => { try { localStorage.setItem('addin_promises_sort', sortBy); } catch { /* ignore */ } }, [sortBy]);
  useEffect(() => { try { localStorage.setItem('addin_promises_bucket', bucket); } catch { /* ignore */ } }, [bucket]);
  useEffect(() => {
    try {
      if (audienceFilter == null) localStorage.removeItem('addin_promises_audience');
      else localStorage.setItem('addin_promises_audience', audienceFilter);
    } catch { /* ignore */ }
  }, [audienceFilter]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  function toggleSelected(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  async function bulkSet(status: 'fulfilled' | 'cancelled') {
    if (selectedIds.size === 0) return;
    const ids = [...selectedIds];
    setBulkBusy(true);
    try {
      // Single batch call replaces N parallel PATCHes.
      const r = await api.addinPromisesBatch(ids, status);
      notifications.show({
        color: status === 'fulfilled' ? 'green' : 'orange',
        message: `${r.affected} promise${r.affected === 1 ? '' : 's'} → ${status}.`,
      });
      setSelectedIds(new Set());
      await load();
    } catch (e) {
      notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Failed' });
    } finally {
      setBulkBusy(false);
    }
  }

  async function load() {
    setBusy(true);
    try {
      setData(await api.addinPromisesList('pending'));
    } catch {
      // silent
    } finally {
      setBusy(false);
    }
  }

  async function scan() {
    setScanning(true);
    try {
      const r = await api.addinPromisesScan(14);
      notifications.show({
        message: `Scanned ${r.scanned} sent emails, extracted ${r.extracted} new commitment${r.extracted === 1 ? '' : 's'}.`,
        color: 'blue',
      });
      await load();
    } catch (e) {
      notifications.show({
        color: 'red',
        message: friendlyAiError(e),
      });
    } finally {
      setScanning(false);
    }
  }

  async function fulfill(id: string) {
    try {
      await api.addinPromisePatch(id, { status: 'fulfilled' });
      await load();
    } catch (e) {
      notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Failed to update' });
    }
  }

  async function cancel(id: string) {
    try {
      await api.addinPromisePatch(id, { status: 'cancelled' });
      await load();
    } catch (e) {
      notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Failed to update' });
    }
  }

  useEffect(() => {
    load();
  }, []);

  const allPromises = data?.promises ?? [];
  const now = Date.now();
  const dayMs = 24 * 60 * 60 * 1000;
  // Top audiences from the loaded promises (case-insensitive bucketing).
  const audienceCounts = new Map<string, number>();
  for (const p of allPromises) {
    if (!p.audience) continue;
    const k = p.audience.toLowerCase();
    audienceCounts.set(k, (audienceCounts.get(k) ?? 0) + 1);
  }
  const topAudiences = [...audienceCounts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([name, count]) => ({ name, count }));
  const promises = allPromises
    .filter((p) => {
      if (audienceFilter && (p.audience ?? '').toLowerCase() !== audienceFilter) return false;
      if (bucket === 'all') return true;
      if (bucket === 'no_due') return !p.due_at;
      if (!p.due_at) return false;
      const due = new Date(p.due_at).getTime();
      if (bucket === 'overdue') return due < now;
      if (bucket === 'today') return due >= now && due < now + dayMs;
      if (bucket === 'week') return due >= now && due < now + 7 * dayMs;
      if (bucket === 'future') return due >= now + 7 * dayMs;
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'due') {
        // Items without a due_at sort to the bottom.
        const da = a.due_at ? new Date(a.due_at).getTime() : Number.POSITIVE_INFINITY;
        const db = b.due_at ? new Date(b.due_at).getTime() : Number.POSITIVE_INFINITY;
        return da - db;
      }
      if (sortBy === 'audience') {
        const aa = (a.audience ?? '').toLowerCase();
        const bb = (b.audience ?? '').toLowerCase();
        if (aa === bb) return new Date(a.detected_at).getTime() - new Date(b.detected_at).getTime();
        if (!aa) return 1;
        if (!bb) return -1;
        return aa.localeCompare(bb);
      }
      return new Date(b.detected_at).getTime() - new Date(a.detected_at).getTime();
    });

  // Bucket counts for the chip row.
  const bucketCounts = {
    all: allPromises.length,
    overdue: allPromises.filter((p) => p.due_at && new Date(p.due_at).getTime() < now).length,
    today: allPromises.filter((p) => p.due_at && (() => {
      const t = new Date(p.due_at).getTime();
      return t >= now && t < now + dayMs;
    })()).length,
    week: allPromises.filter((p) => p.due_at && (() => {
      const t = new Date(p.due_at).getTime();
      return t >= now && t < now + 7 * dayMs;
    })()).length,
    future: allPromises.filter((p) => p.due_at && new Date(p.due_at).getTime() >= now + 7 * dayMs).length,
    no_due: allPromises.filter((p) => !p.due_at).length,
  };
  if (busy && !data) return null;

  // Show even when empty so users can scan
  return (
    <Card
      padding="md"
      radius="md"
      withBorder
      style={{ borderLeft: '4px solid var(--mantine-color-teal-6)' }}
    >
      <Stack gap="xs">
        <Group justify="space-between" wrap="nowrap">
          <Stack gap={0}>
            <Group gap="xs">
              <IconCheck {...ICON_PROPS} />
              <Text fw={600}>Forgotten promises</Text>
              {promises.length > 0 && <Badge color="teal">{promises.length}</Badge>}
            </Group>
            <Text size="xs" c="dimmed">
              Things you said you'd do — extracted from your sent mail
            </Text>
          </Stack>
          <Group gap={4}>
            {promises.length > 0 && (
              <Button
                size="xs"
                variant="subtle"
                leftSection={<IconFileText size={12} />}
                onClick={() => {
                  const esc = (s: string | number | null | undefined) => {
                    if (s == null) return '';
                    const v = String(s);
                    return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
                  };
                  const header = ['promise_text', 'maker', 'audience', 'due_at', 'subject', 'detected_at', 'source_message_id'].join(',');
                  // Filter-aware: export currently-visible rows (after bucket
                  // + audience filter + sort).
                  const lines = [
                    header,
                    ...promises.map((p) => [
                      p.promise_text, p.maker, p.audience, p.due_at, p.subject, p.detected_at, p.source_message_id,
                    ].map(esc).join(',')),
                  ];
                  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  // Filename encodes the active filter so multiple exports don't collide.
                  const tag = [
                    bucket !== 'all' ? bucket : null,
                    audienceFilter ? `to-${audienceFilter.replace(/[^a-z0-9]/gi, '_').slice(0, 24)}` : null,
                  ].filter(Boolean).join('-');
                  a.download = `promises${tag ? `-${tag}` : ''}-${new Date().toISOString().slice(0, 10)}.csv`;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  URL.revokeObjectURL(url);
                }}
              >
                CSV ({promises.length})
              </Button>
            )}
            {promises.filter((p) => p.due_at).length > 0 && (
              <Button
                size="xs"
                variant="subtle"
                leftSection={<IconClock size={12} />}
                onClick={() => {
                  // Build .ics for promises with a due date. Each becomes a VTODO.
                  const dated = promises.filter((p) => p.due_at);
                  const fmt = (iso: string) => iso.replace(/[-:]/g, '').replace(/\.\d+/, '').slice(0, 15) + 'Z';
                  const lines = [
                    'BEGIN:VCALENDAR',
                    'VERSION:2.0',
                    'PRODID:-//ai-outlook//Promises//EN',
                    'CALSCALE:GREGORIAN',
                    ...dated.flatMap((p) => [
                      'BEGIN:VTODO',
                      `UID:${p.id}@ai-outlook`,
                      `DTSTAMP:${fmt(new Date().toISOString())}`,
                      `DUE:${fmt(p.due_at!)}`,
                      `SUMMARY:${p.promise_text.replace(/[\r\n]/g, ' ').slice(0, 200)}`,
                      `DESCRIPTION:${(p.audience ? `To: ${p.audience}\\n` : '') + (p.subject ? `Re: ${p.subject}` : '')}`,
                      'STATUS:NEEDS-ACTION',
                      'END:VTODO',
                    ]),
                    'END:VCALENDAR',
                  ];
                  const blob = new Blob([lines.join('\r\n')], { type: 'text/calendar;charset=utf-8' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `promises-${new Date().toISOString().slice(0, 10)}.ics`;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  URL.revokeObjectURL(url);
                }}
              >
                ICS ({promises.filter((p) => p.due_at).length})
              </Button>
            )}
            <Button
              size="xs"
              variant="default"
              onClick={scan}
              loading={scanning}
              leftSection={<IconSparkles {...ICON_PROPS} />}
            >
              Scan sent mail
            </Button>
          </Group>
        </Group>
        {/* Mini-stats summary line — total / overdue / today / no-due. */}
        {allPromises.length > 0 && (
          <Group gap="md" wrap="wrap">
            <Text size="xs" c="dimmed"><b>{allPromises.length}</b> total</Text>
            {bucketCounts.overdue > 0 && <Text size="xs" c="red"><b>{bucketCounts.overdue}</b> overdue</Text>}
            {bucketCounts.today > 0 && <Text size="xs" c="orange"><b>{bucketCounts.today}</b> due today</Text>}
            {bucketCounts.week > 0 && <Text size="xs" c="yellow"><b>{bucketCounts.week}</b> this week</Text>}
            {bucketCounts.no_due > 0 && <Text size="xs" c="dimmed"><b>{bucketCounts.no_due}</b> no due date</Text>}
          </Group>
        )}
        {/* Bulk action bar — shows when at least one promise is selected. */}
        {selectedIds.size > 0 && (
          <Paper p="xs" withBorder
            style={{ background: 'var(--mantine-color-teal-0)', borderLeft: '3px solid var(--mantine-color-teal-6)' }}>
            <Group justify="space-between" wrap="nowrap" gap="xs">
              <Text size="xs" fw={500}>{selectedIds.size} selected</Text>
              <Group gap={4}>
                <Button size="compact-xs" color="green" leftSection={<IconCheck size={12} />}
                  loading={bulkBusy} onClick={() => bulkSet('fulfilled')}>
                  Mark fulfilled
                </Button>
                <Button size="compact-xs" color="orange" variant="light"
                  loading={bulkBusy} onClick={() => bulkSet('cancelled')}>
                  Cancel
                </Button>
                <Button size="compact-xs" variant="default" leftSection={<IconFileText size={12} />}
                  onClick={() => {
                    const ids = selectedIds;
                    const rows = (allPromises ?? []).filter((p) => ids.has(p.id));
                    const text = rows.map((p) => [
                      p.promise_text,
                      p.audience ? `  To: ${p.audience}` : '',
                      p.due_at ? `  Due: ${p.due_at.slice(0, 10)}` : '',
                    ].filter(Boolean).join('\n')).join('\n\n');
                    navigator.clipboard.writeText(text)
                      .then(() => notifications.show({ message: `Copied ${rows.length} promise${rows.length === 1 ? '' : 's'}.` }))
                      .catch(() => notifications.show({ color: 'red', message: 'Copy failed' }));
                  }}>
                  Copy text
                </Button>
                <Button size="compact-xs" variant="default" leftSection={<IconClock size={12} />}
                  onClick={() => {
                    const ids = selectedIds;
                    const rows = (allPromises ?? []).filter((p) => ids.has(p.id) && p.due_at);
                    if (rows.length === 0) {
                      notifications.show({ color: 'orange', message: 'Selected items have no due dates.' });
                      return;
                    }
                    const fmt = (iso: string) => iso.replace(/[-:]/g, '').replace(/\.\d+/, '').slice(0, 15) + 'Z';
                    const lines = [
                      'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//ai-outlook//Promises//EN', 'CALSCALE:GREGORIAN',
                      ...rows.flatMap((p) => [
                        'BEGIN:VTODO',
                        `UID:${p.id}@ai-outlook`,
                        `DTSTAMP:${fmt(new Date().toISOString())}`,
                        `DUE:${fmt(p.due_at!)}`,
                        `SUMMARY:${p.promise_text.replace(/[\r\n]/g, ' ').slice(0, 200)}`,
                        `DESCRIPTION:${(p.audience ? `To: ${p.audience}\\n` : '') + (p.subject ? `Re: ${p.subject}` : '')}`,
                        'STATUS:NEEDS-ACTION',
                        'END:VTODO',
                      ]),
                      'END:VCALENDAR',
                    ];
                    const blob = new Blob([lines.join('\r\n')], { type: 'text/calendar;charset=utf-8' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `promises-selected-${new Date().toISOString().slice(0, 10)}.ics`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    URL.revokeObjectURL(url);
                  }}>
                  ICS
                </Button>
                <Button size="compact-xs" variant="subtle" color="gray"
                  onClick={() => setSelectedIds(new Set())}>
                  Clear
                </Button>
              </Group>
            </Group>
          </Paper>
        )}
        {/* Filter chips + sort selector */}
        {allPromises.length > 0 && (
          <Group gap={4} wrap="wrap" justify="space-between">
            <Group gap={4} wrap="wrap">
              {([
                { k: 'all', label: 'All' },
                { k: 'overdue', label: 'Overdue', color: 'red' },
                { k: 'today', label: 'Today', color: 'orange' },
                { k: 'week', label: 'This week', color: 'yellow' },
                { k: 'future', label: 'Future', color: 'blue' },
                { k: 'no_due', label: 'No due date', color: 'gray' },
              ] as const).map((b) => (
                <Badge
                  key={b.k}
                  size="sm"
                  variant={bucket === b.k ? 'filled' : 'light'}
                  color={('color' in b ? b.color : 'teal') as string}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setBucket(b.k as typeof bucket)}
                >
                  {b.label} {bucketCounts[b.k]}
                </Badge>
              ))}
            </Group>
            <SegmentedControl
              size="xs"
              value={sortBy}
              onChange={(v) => setSortBy(v as 'due' | 'detected' | 'audience')}
              data={[
                { value: 'due', label: 'Due date' },
                { value: 'detected', label: 'Detected' },
                { value: 'audience', label: 'Audience' },
              ]}
            />
          </Group>
        )}
        {/* By-audience sub-strip: top 3 recipients with counts */}
        {topAudiences.length > 0 && (
          <Group gap={4} wrap="wrap">
            <Text size="xs" c="dimmed">To:</Text>
            <Badge size="xs" variant={audienceFilter === null ? 'filled' : 'light'} color="grape"
              style={{ cursor: 'pointer' }} onClick={() => setAudienceFilter(null)}>
              Anyone {allPromises.filter((p) => p.audience).length}
            </Badge>
            {topAudiences.map((a) => (
              <Badge
                key={a.name}
                size="xs"
                variant={audienceFilter === a.name ? 'filled' : 'light'}
                color="grape"
                style={{ cursor: 'pointer', maxWidth: 180 }}
                onClick={() => setAudienceFilter(audienceFilter === a.name ? null : a.name)}
              >
                <Text size="xs" lineClamp={1}>{a.name} {a.count}</Text>
              </Badge>
            ))}
          </Group>
        )}
        {promises.length === 0 && !scanning && (
          <Text size="xs" c="dimmed" ta="center" py="md">
            {allPromises.length === 0
              ? 'No tracked commitments. Click "Scan sent mail" to extract from the last 14 days.'
              : 'No promises match this filter. Pick a different chip above.'}
          </Text>
        )}
        {promises.slice(0, 12).map((p) => (
          <Paper key={p.id} p="xs" withBorder
            // Round 36 follow-up: right-click on a promise row opens the
            // source email in Outlook. Mirrors the existing pattern in
            // Action Inbox + Briefing rows (openEmailInOutlook helper).
            // User feedback: "I can't right click and open the email in here".
            onContextMenu={(e) => {
              e.preventDefault();
              openEmailInOutlook({
                message_id: p.source_message_id,
                subject: p.subject ?? null,
              });
            }}
            title="Right-click to open the source email in Outlook"
            style={{
              background: selectedIds.has(p.id) ? 'var(--mantine-color-teal-0)' : undefined,
              cursor: 'context-menu',
            }}>
            <Group justify="space-between" wrap="nowrap" gap="xs" align="flex-start">
              <Checkbox
                size="xs"
                checked={selectedIds.has(p.id)}
                onChange={() => toggleSelected(p.id)}
                aria-label="select promise for bulk"
                mt={4}
              />
              <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                <Text size="sm" fw={500}>
                  {p.promise_text}
                </Text>
                <Group gap={4} wrap="wrap">
                  <Text size="xs" c="dimmed" lineClamp={1} style={{ flex: 1, minWidth: 0 }}>
                    Re: {p.subject ?? '(no subject)'}
                    {p.audience && ` · to ${p.audience}`}
                  </Text>
                  {/* Inline due-date editor — click badge to pop a date prompt. */}
                  <Badge
                    size="xs"
                    variant="light"
                    color={p.due_at ? (new Date(p.due_at) < new Date() ? 'red' : 'orange') : 'gray'}
                    style={{ cursor: 'pointer' }}
                    onClick={async (e) => {
                      e.stopPropagation();
                      const cur = p.due_at ? p.due_at.slice(0, 10) : '';
                      const v = prompt('Due date (YYYY-MM-DD), empty to clear:', cur);
                      if (v === null) return;
                      const t = v.trim();
                      const due_at = t ? new Date(`${t}T17:00:00Z`).toISOString() : null;
                      if (t && Number.isNaN(Date.parse(t))) {
                        notifications.show({ color: 'red', message: 'Invalid date format.' });
                        return;
                      }
                      try {
                        await api.addinPromisePatch(p.id, { due_at });
                        notifications.show({ message: due_at ? `Due ${t}.` : 'Due-date cleared.' });
                        load();
                      } catch (err) {
                        notifications.show({ color: 'red', message: err instanceof Error ? err.message : 'Failed' });
                      }
                    }}
                  >
                    {p.due_at ? `due ${shortDate(p.due_at)}` : 'no due date'}
                  </Badge>
                </Group>
              </Stack>
              <Group gap={4}>
                <Tooltip label="Edit promise text">
                  <ActionIcon variant="subtle" color="gray" onClick={async () => {
                    const next = prompt('Edit promise text:', p.promise_text);
                    if (next === null || next.trim() === p.promise_text.trim()) return;
                    try {
                      await api.addinPromisePatch(p.id, { promise_text: next.trim() });
                      notifications.show({ message: 'Promise updated.' });
                      load();
                    } catch (e) {
                      notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Failed' });
                    }
                  }}>
                    <IconPencil {...ICON_PROPS} />
                  </ActionIcon>
                </Tooltip>
                <Menu position="bottom-end">
                  <Menu.Target>
                    <Tooltip label="Snooze due date">
                      <ActionIcon variant="subtle" color="orange">
                        <IconClock {...ICON_PROPS} />
                      </ActionIcon>
                    </Tooltip>
                  </Menu.Target>
                  <Menu.Dropdown>
                    {[
                      { label: '+1 day', days: 1 },
                      { label: '+3 days', days: 3 },
                      { label: '+1 week', days: 7 },
                      { label: '+2 weeks', days: 14 },
                      { label: '+1 month', days: 30 },
                    ].map((s) => (
                      <Menu.Item key={s.days} onClick={async () => {
                        const base = p.due_at ? new Date(p.due_at).getTime() : Date.now();
                        const next = new Date(base + s.days * 86400000).toISOString();
                        try {
                          await api.addinPromisePatch(p.id, { due_at: next });
                          notifications.show({ message: `Snoozed ${s.label}.` });
                          load();
                        } catch (e) {
                          notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Failed' });
                        }
                      }}>
                        {s.label}
                      </Menu.Item>
                    ))}
                  </Menu.Dropdown>
                </Menu>
                <Tooltip label="Mark fulfilled">
                  <ActionIcon variant="subtle" color="teal" onClick={() => fulfill(p.id)}>
                    <IconCheck {...ICON_PROPS} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="Cancel / no longer relevant">
                  <ActionIcon variant="subtle" color="gray" onClick={() => cancel(p.id)}>
                    <IconX {...ICON_PROPS} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="Open original email">
                  <ActionIcon
                    variant="subtle"
                    onClick={() => postToHost({ type: 'open_in_outlook', message_id: p.source_message_id })}
                  >
                    <IconMail {...ICON_PROPS} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="Copy as text">
                  <ActionIcon variant="subtle" color="gray" onClick={() => {
                    const lines = [
                      p.promise_text,
                      p.audience ? `To: ${p.audience}` : '',
                      p.subject ? `Re: ${p.subject}` : '',
                      p.due_at ? `Due: ${p.due_at.slice(0, 10)}` : '',
                    ].filter(Boolean).join('\n');
                    navigator.clipboard.writeText(lines)
                      .then(() => notifications.show({ message: 'Promise copied.' }))
                      .catch(() => notifications.show({ color: 'red', message: 'Copy failed' }));
                  }}>
                    <IconFileText {...ICON_PROPS} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            </Group>
          </Paper>
        ))}
        {promises.length > 6 && (
          <Text size="xs" c="dimmed" ta="center" mt={4}>
            +{promises.length - 6} more pending
          </Text>
        )}
      </Stack>
    </Card>
  );
}

// =========================================================================
// Radar panel — early-warning feed. Round 39 points 21–25.
// Surfaces new risks, new senders into existing threads, topic spikes,
// and silent customers we used to talk to often.
// =========================================================================

export function RadarPanel(): React.JSX.Element {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.addinRadar>> | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    setBusy(true);
    setErr(null);
    try {
      setData(await api.addinRadar());
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not load radar');
    } finally {
      setBusy(false);
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: load on mount
  useEffect(() => {
    load();
  }, []);

  if (busy && !data) {
    return (
      <Center p="xl">
        <Loader />
      </Center>
    );
  }
  if (err) {
    return (
      <Alert color="red" variant="light">
        {err}
        <Button size="xs" onClick={load} ml="sm">
          Retry
        </Button>
      </Alert>
    );
  }
  if (!data) {
    return (
      <Alert color="yellow" variant="light">
        No radar data yet — index your mailboxes first.
      </Alert>
    );
  }

  const total =
    data.new_risks.length +
    data.new_relationships.length +
    data.topic_spikes.length +
    data.silent_customers.length;

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Stack gap={0}>
          <Title order={4}>Email radar</Title>
          <Text size="xs" c="dimmed">
            {total} signal{total === 1 ? '' : 's'} detected · refreshed {formatRelative(data.generated_at)}
          </Text>
        </Stack>
        <Group gap={4}>
          {total > 0 && (
            <Button size="xs" variant="subtle" leftSection={<IconFileText size={12} />}
              onClick={() => {
                const esc = (s: string | number | null | undefined) => {
                  if (s == null) return '';
                  const v = String(s);
                  return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
                };
                const lines: string[] = [];
                lines.push('# new_risks');
                lines.push('message_id,subject,from,received_at');
                for (const r of data.new_risks) lines.push([r.message_id, r.subject, r.from_addr, r.received_at].map(esc).join(','));
                lines.push('', '# new_relationships');
                lines.push('thread_id,from,first_seen_in_thread,subject');
                for (const r of data.new_relationships) lines.push([r.thread_id, r.from_addr, r.first_seen_in_thread, r.subject].map(esc).join(','));
                lines.push('', '# topic_spikes');
                lines.push('from,n_today,n_baseline_avg,multiplier');
                for (const t of data.topic_spikes) lines.push([t.from_addr, t.n_today, t.n_baseline_avg, t.multiplier].map(esc).join(','));
                lines.push('', '# silent_customers');
                lines.push('from,last_received,total');
                for (const s of data.silent_customers) lines.push([s.from_addr, s.last_received, s.total].map(esc).join(','));
                const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `radar-${new Date().toISOString().slice(0, 10)}.csv`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
              }}>
              CSV
            </Button>
          )}
          <Button size="xs" variant="default" onClick={load} loading={busy}>
            Refresh
          </Button>
        </Group>
      </Group>

      {total === 0 && (
        <Card padding="xl" radius="md" withBorder>
          <Stack align="center" gap={4}>
            <Text fw={600} size="lg">
              ✓ Quiet skies
            </Text>
            <Text size="sm" c="dimmed">
              No new risks, relationship shifts, topic spikes, or silent customers detected.
            </Text>
          </Stack>
        </Card>
      )}

      {data.new_risks.length > 0 && (
        <Card
          padding="md"
          radius="md"
          withBorder
          style={{ borderLeft: '4px solid var(--mantine-color-red-6)' }}
        >
          <Stack gap="xs">
            <Stack gap={0}>
              <Group gap="xs">
                <IconAlertTriangle {...ICON_PROPS} />
                <Text fw={600}>New financial / banking signals</Text>
                <Badge color="red">{data.new_risks.length}</Badge>
              </Group>
              <Text size="xs" c="dimmed">
                Last 7 days. Bank-detail change attempts → verify out-of-band before acting.
              </Text>
            </Stack>
            {data.new_risks.slice(0, 8).map((r) => (
              <Paper
                key={r.message_id}
                p="xs"
                style={{ cursor: 'pointer' }}
                onClick={() => postToHost({ type: 'open_in_outlook', message_id: r.message_id })}
              >
                <Stack gap={0}>
                  <Text size="sm" fw={500} lineClamp={1}>
                    {r.subject ?? '(no subject)'}
                  </Text>
                  <Text size="xs" c="dimmed" lineClamp={1}>
                    {r.from_addr} · {shortDate(r.received_at)}
                  </Text>
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Card>
      )}

      {data.new_relationships.length > 0 && (
        <Card
          padding="md"
          radius="md"
          withBorder
          style={{ borderLeft: '4px solid var(--mantine-color-blue-6)' }}
        >
          <Stack gap="xs">
            <Stack gap={0}>
              <Group gap="xs">
                <IconUserPlus {...ICON_PROPS} />
                <Text fw={600}>New senders in active threads</Text>
                <Badge color="blue">{data.new_relationships.length}</Badge>
              </Group>
              <Text size="xs" c="dimmed">
                Someone new joined an ongoing conversation in the last 24h.
              </Text>
            </Stack>
            {data.new_relationships.slice(0, 8).map((r) => (
              <Paper key={r.thread_id + r.from_addr} p="xs">
                <Stack gap={0}>
                  <Text size="sm" fw={500} lineClamp={1}>
                    {r.subject ?? '(no subject)'}
                  </Text>
                  <Text size="xs" c="dimmed" lineClamp={1}>
                    {r.from_addr} · joined {formatRelative(r.first_seen_in_thread)}
                  </Text>
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Card>
      )}

      {data.topic_spikes.length > 0 && (
        <Card
          padding="md"
          radius="md"
          withBorder
          style={{ borderLeft: '4px solid var(--mantine-color-orange-6)' }}
        >
          <Stack gap="xs">
            <Stack gap={0}>
              <Group gap="xs">
                <IconRadar {...ICON_PROPS} />
                <Text fw={600}>Volume spikes</Text>
                <Badge color="orange">{data.topic_spikes.length}</Badge>
              </Group>
              <Text size="xs" c="dimmed">
                Senders sending 2.5× their 30-day baseline today — incident, escalation, or campaign.
              </Text>
            </Stack>
            {data.topic_spikes.map((s) => (
              <Paper key={s.from_addr} p="xs">
                <Group justify="space-between" wrap="nowrap">
                  <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                    <Text size="sm" fw={500} lineClamp={1}>
                      {s.from_addr}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {s.n_today} today vs {s.n_baseline_avg.toFixed(1)}/day baseline
                    </Text>
                  </Stack>
                  <Badge color="orange" variant="light">
                    {s.multiplier.toFixed(1)}×
                  </Badge>
                </Group>
              </Paper>
            ))}
          </Stack>
        </Card>
      )}

      {data.silent_customers.length > 0 && (
        <Card
          padding="md"
          radius="md"
          withBorder
          style={{ borderLeft: '4px solid var(--mantine-color-grape-6)' }}
        >
          <Stack gap="xs">
            <Stack gap={0}>
              <Group gap="xs">
                <IconClock {...ICON_PROPS} />
                <Text fw={600}>Silent customers</Text>
                <Badge color="grape">{data.silent_customers.length}</Badge>
              </Group>
              <Text size="xs" c="dimmed">
                People you used to correspond with regularly — quiet 14+ days. Nudge them.
              </Text>
            </Stack>
            {data.silent_customers.slice(0, 10).map((s) => (
              <Paper key={s.from_addr} p="xs">
                <Group justify="space-between" wrap="nowrap">
                  <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                    <Text size="sm" fw={500} lineClamp={1}>
                      {s.from_addr}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {s.total} historical exchanges · last heard {formatRelative(s.last_received)}
                    </Text>
                  </Stack>
                </Group>
              </Paper>
            ))}
          </Stack>
        </Card>
      )}
    </Stack>
  );
}

// =========================================================================
// Ask my mailbox — natural-language Q&A over indexed emails. Round 40 #9.
// =========================================================================


export const SCORE_HISTORY: Array<{ ts: number; score: number }> = (() => {
  try {
    const raw = JSON.parse(localStorage.getItem('addin_score_history') ?? '[]');
    if (Array.isArray(raw)) return raw.slice(-40) as Array<{ ts: number; score: number }>;
  } catch { /* ignore */ }
  return [];
})();
export function pushScoreSnapshot(score: number) {
  // Avoid duplicates within 60s.
  const last = SCORE_HISTORY[SCORE_HISTORY.length - 1];
  if (last && Date.now() - last.ts < 60_000) return;
  SCORE_HISTORY.push({ ts: Date.now(), score });
  if (SCORE_HISTORY.length > 40) SCORE_HISTORY.shift();
  try { localStorage.setItem('addin_score_history', JSON.stringify(SCORE_HISTORY)); } catch { /* ignore */ }
}

export function InboxControlScoreCard({
  health,
  onSwitchTab,
}: {
  health: NonNullable<Awaited<ReturnType<typeof api.addinHealth>>>;
  onSwitchTab: (t: string) => void;
}): React.JSX.Element {
  // biome-ignore lint/correctness/useExhaustiveDependencies: snapshot on score change
  useEffect(() => { pushScoreSnapshot(health.score); }, [health.score]);
  const color =
    health.score >= 80 ? 'green' : health.score >= 60 ? 'blue' :
    health.score >= 40 ? 'yellow' : 'red';

  function tabFor(action_kind: string): string {
    if (action_kind === 'open_action_inbox_act') return 'action';
    if (action_kind === 'open_followups') return 'followups';
    if (action_kind === 'open_risk_feed') return 'action';
    if (action_kind === 'open_promises') return 'promises';
    return 'action';
  }

  const buckets = health.buckets ?? [];
  const path = health.improvement_path ?? [];
  const issuesCount = buckets.reduce((acc, b) => acc + b.count, 0);

  return (
    <Card padding="md" radius="md" withBorder
      style={{ borderLeft: `4px solid var(--mantine-color-${color}-6)` }}>
      <Stack gap="md">
        <Group justify="space-between" wrap="wrap" align="flex-start">
          <Group gap="md" wrap="nowrap">
            <RingProgress
              size={84}
              thickness={8}
              sections={[{ value: health.score, color }]}
              label={
                <Stack gap={0} align="center">
                  <Text ta="center" fw={700} size="lg">{health.score}</Text>
                  <Text ta="center" size="xs" c="dimmed">/100</Text>
                </Stack>
              }
            />
            <Stack gap={0}>
              <Text fw={700} size="lg">Inbox Control Score</Text>
              {(() => {
                // Show ↑/↓ delta vs the previous distinct snapshot.
                const prev = SCORE_HISTORY.length >= 2 ? SCORE_HISTORY[SCORE_HISTORY.length - 2] : null;
                const delta = prev ? health.score - prev.score : 0;
                return (
                  <Group gap={4}>
                    <Text size="xs" c="dimmed">
                      Grade {health.grade} ·{' '}
                      {issuesCount === 0
                        ? 'No active issues — you are in control.'
                        : `${issuesCount} item${issuesCount === 1 ? '' : 's'} across ${buckets.filter((b) => b.count > 0).length} bucket${buckets.filter((b) => b.count > 0).length === 1 ? '' : 's'}`}
                    </Text>
                    {prev && delta !== 0 && (
                      <Tooltip label={`Last snapshot: ${prev.score} (${formatRelative(new Date(prev.ts).toISOString())})`}>
                        <Badge size="xs" color={delta > 0 ? 'green' : 'red'} variant="light">
                          {delta > 0 ? '↑' : '↓'} {Math.abs(delta)}
                        </Badge>
                      </Tooltip>
                    )}
                  </Group>
                );
              })()}
              {/* History sparkline — last N score snapshots, click to reset */}
              {SCORE_HISTORY.length > 1 && (
                <Tooltip label={`Score history: ${SCORE_HISTORY.map((s) => s.score).join(' → ')} · click to reset`}>
                  <Group
                    gap={1} align="flex-end" h={16} mt={2}
                    style={{ width: 96, cursor: 'pointer' }}
                    onClick={() => {
                      if (!confirm('Reset score history?')) return;
                      SCORE_HISTORY.length = 0;
                      try { localStorage.removeItem('addin_score_history'); } catch { /* ignore */ }
                      notifications.show({ message: 'Score history reset.' });
                    }}
                  >
                    {SCORE_HISTORY.map((s, i) => (
                      <div key={i} style={{
                        flex: 1,
                        height: `${Math.max(2, s.score)}%`,
                        background: `var(--mantine-color-${color}-5)`,
                        borderRadius: 1,
                      }} />
                    ))}
                  </Group>
                </Tooltip>
              )}
            </Stack>
          </Group>
          <Button
            size="compact-xs"
            variant="subtle"
            leftSection={<IconCommand size={12} />}
            onClick={() => {
              const lines = [
                `Inbox Control Score: ${health.score}/100 (grade ${health.grade})`,
                '',
                ...buckets
                  .filter((b) => b.count > 0)
                  .map((b) => `- ${b.label}: ${b.count} (-${b.penalty} pts)`),
              ];
              if (issuesCount === 0) lines.push('- No active issues');
              navigator.clipboard.writeText(lines.join('\n'))
                .then(() => notifications.show({ message: 'Score summary copied.' }))
                .catch(() => notifications.show({ color: 'red', message: 'Could not copy' }));
            }}
          >
            Copy summary
          </Button>
        </Group>

        {/* Improvement path — top 3 highest-recovery actions */}
        {path.length > 0 && (
          <Stack gap={4}>
            <Text size="xs" c="dimmed" tt="uppercase">Quickest wins to recover points</Text>
            {path.map((p, i) => (
              <Paper key={`${p.bucket}-${i}`} p="xs" withBorder
                style={{ borderLeft: `3px solid var(--mantine-color-${color}-5)` }}>
                <Group justify="space-between" wrap="nowrap">
                  <Group gap="xs" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                    <Badge size="sm" color={color} variant="filled">{p.impact}</Badge>
                    <Text size="sm" lineClamp={1}>{p.label}</Text>
                  </Group>
                  <Button size="compact-xs" variant="light" color={color}
                    onClick={() => onSwitchTab(tabFor(p.action_kind))}>
                    Open
                  </Button>
                </Group>
              </Paper>
            ))}
          </Stack>
        )}

        {/* Full breakdown — all buckets, clickable to deep-link. */}
        <SimpleGrid cols={{ base: 2, sm: 5 }} spacing="xs">
          {buckets.map((b) => {
            const interactive = b.count > 0 && b.action_kind;
            const target = b.action_kind ? tabFor(b.action_kind) : null;
            return (
              <Stack
                key={b.key}
                gap={0}
                align="center"
                style={{ cursor: interactive ? 'pointer' : 'default', borderRadius: 4, padding: 4 }}
                onClick={() => { if (interactive && target) onSwitchTab(target); }}
                onMouseEnter={(e) => { if (interactive) (e.currentTarget as HTMLElement).style.background = 'var(--mantine-color-gray-1)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = ''; }}
                title={interactive ? `Click → ${target}` : undefined}
              >
                <Text fw={700} size="lg" c={b.count > 0 ? 'red' : 'dimmed'}>
                  {b.count}
                </Text>
                <Text size="xs" c="dimmed" ta="center" lineClamp={2}>{b.label}</Text>
                {b.penalty > 0 && (
                  <Text size="xs" c="red">-{b.penalty}</Text>
                )}
              </Stack>
            );
          })}
        </SimpleGrid>
      </Stack>
    </Card>
  );
}

// Phase 9 — Morning Brief 2.0: yesterday recap, today top 3, risk alerts,
// pending decisions, promises due today. Coexists with the older
// MorningBriefBanner (Round 39) which uses the simpler /addin/morning endpoint.
export function MorningBriefV2Card(): React.JSX.Element | null {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.addinMorningBrief>> | null>(null);
  const [stateCounts, setStateCounts] = useState<Awaited<ReturnType<typeof api.addinActionInbox5State>>['counts'] | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Send the user's local timezone so greeting + brief reflect their day.
    let tz: string | undefined;
    try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { /* fall back to default */ }
    api.addinMorningBrief(tz)
      .then((r) => { if (!cancelled) setData(r); })
      .catch(() => { /* silent */ });
    api.addinActionInbox5State()
      .then((r) => { if (!cancelled) setStateCounts(r.counts); })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, []);

  if (!data) return null;
  const r = data.yesterday_recap;
  const interesting =
    data.today_top_3.length > 0 ||
    data.risk_alerts.length > 0 ||
    data.promises_due_today.length > 0 ||
    r.sent_by_me + r.incoming + r.promises_resolved + r.rules_or_cases_activated + r.undos > 0;
  if (!interesting) return null;

  return (
    <Card padding="md" radius="md" withBorder
      style={{ borderLeft: '4px solid var(--mantine-color-grape-6)' }}>
      <Stack gap="md">
        <Group gap="xs">
          <ThemeIcon variant="light" color="grape">
            <IconSparkles {...ICON_PROPS} />
          </ThemeIcon>
          <Text fw={600}>{data.greeting}. Briefing — {data.timezone}.</Text>
        </Group>

        {/* Action Inbox state counts — clickable to deep-link */}
        {stateCounts && (
          <Group gap={4} wrap="wrap">
            <Text size="xs" c="dimmed">Inbox:</Text>
            {(['act', 'wait', 'know', 'automate', 'ignore'] as const).map((s) => {
              const meta = STATE_META[s];
              return (
                <Badge
                  key={s}
                  size="sm"
                  color={meta.color}
                  variant={stateCounts[s] > 0 ? 'light' : 'outline'}
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    sessionStorage.setItem('addin_action_state', s);
                    window.dispatchEvent(new CustomEvent('ai-outlook:switch-tab', { detail: 'action' }));
                  }}
                >
                  {meta.label} {stateCounts[s]}
                </Badge>
              );
            })}
          </Group>
        )}

        {/* Yesterday recap — 5 metric tiles */}
        <SimpleGrid cols={{ base: 2, sm: 5 }} spacing="xs">
          <Stack gap={0} align="center">
            <Text fw={700} size="lg">{r.sent_by_me}</Text>
            <Text size="xs" c="dimmed">you sent</Text>
          </Stack>
          <Stack gap={0} align="center">
            <Text fw={700} size="lg">{r.incoming}</Text>
            <Text size="xs" c="dimmed">arrived</Text>
          </Stack>
          <Stack gap={0} align="center">
            <Text fw={700} size="lg" c={r.promises_resolved > 0 ? 'green' : 'dimmed'}>
              {r.promises_resolved}
            </Text>
            <Text size="xs" c="dimmed">promises closed</Text>
          </Stack>
          <Stack gap={0} align="center">
            <Text fw={700} size="lg" c={r.rules_or_cases_activated > 0 ? 'blue' : 'dimmed'}>
              {r.rules_or_cases_activated}
            </Text>
            <Text size="xs" c="dimmed">rules/cases live</Text>
          </Stack>
          <Stack gap={0} align="center">
            <Text fw={700} size="lg" c={r.undos > 0 ? 'orange' : 'dimmed'}>{r.undos}</Text>
            <Text size="xs" c="dimmed">undos</Text>
          </Stack>
        </SimpleGrid>

        {/* Today top 3 */}
        {data.today_top_3.length > 0 && (
          <Stack gap={4}>
            <Text size="xs" c="dimmed" tt="uppercase">Top 3 today</Text>
            {data.today_top_3.map((t) => (
              <Paper key={t.message_id} p="xs" withBorder>
                <Group gap="xs" wrap="nowrap">
                  {t.is_vip ? <Badge size="xs" color="yellow">VIP</Badge> :
                    t.classification_tier ?
                      <Badge size="xs" color={t.classification_tier === 'P0' ? 'red' : t.classification_tier === 'P1' ? 'orange' : 'gray'}>
                        {t.classification_tier}
                      </Badge> : null}
                  <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                    <Text size="sm" fw={500} lineClamp={1}>{t.subject ?? '(no subject)'}</Text>
                    <Text size="xs" c="dimmed" lineClamp={1}>
                      {t.from_addr} · {formatRelative(t.received_at)}
                    </Text>
                  </Stack>
                </Group>
              </Paper>
            ))}
          </Stack>
        )}

        {/* Brain-extracted: decisions/approvals/sign-offs needed (intent: decide/approve/sign/review). */}
        {((data as never as { ai_decisions_needed?: Array<{ message_id: string; subject: string | null; from_addr: string; received_at: string; summary: string | null; intent: string | null; user_owes: string | null; proposed_action: string | null }> }).ai_decisions_needed ?? []).length > 0 && (
          <Stack gap={4}>
            <Text size="xs" c="violet" tt="uppercase" fw={600}>Decisions needed</Text>
            {((data as never as { ai_decisions_needed: Array<{ message_id: string; subject: string | null; from_addr: string; received_at: string; summary: string | null; intent: string | null; user_owes: string | null; proposed_action: string | null }> }).ai_decisions_needed).slice(0, 5).map((d) => (
              <Paper key={d.message_id} p="xs" withBorder
                style={{ borderLeft: '3px solid var(--mantine-color-violet-5)' }}>
                <Stack gap={2}>
                  <Group gap="xs" wrap="nowrap">
                    <Badge size="xs" color="violet" variant="light">{d.intent ?? 'decide'}</Badge>
                    <Text size="sm" fw={500} lineClamp={1} style={{ flex: 1 }}>
                      {d.summary ?? d.subject ?? '(no subject)'}
                    </Text>
                  </Group>
                  <Text size="xs" c="dimmed" lineClamp={1}>{d.from_addr} · {formatRelative(d.received_at)}</Text>
                  {d.user_owes && <Text size="xs" c="violet">You owe: {d.user_owes}</Text>}
                  {d.proposed_action && <Text size="xs" c="dimmed" fs="italic">→ {d.proposed_action}</Text>}
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}

        {/* Brain-extracted: deadlines from analysis.deadlines[]. */}
        {((data as never as { ai_deadlines_today?: Array<{ message_id: string; subject: string | null; from_addr: string; received_at: string; summary: string | null; deadlines_json: string | null }> }).ai_deadlines_today ?? []).length > 0 && (
          <Stack gap={4}>
            <Text size="xs" c="orange" tt="uppercase" fw={600}>Active deadlines</Text>
            {((data as never as { ai_deadlines_today: Array<{ message_id: string; subject: string | null; from_addr: string; received_at: string; summary: string | null; deadlines_json: string | null }> }).ai_deadlines_today).slice(0, 5).map((d) => {
              let deadlines: Array<{ text: string; due_iso?: string | null }> = [];
              if (d.deadlines_json) { try { deadlines = JSON.parse(d.deadlines_json); } catch { /* ignore */ } }
              return (
                <Paper key={d.message_id} p="xs" withBorder
                  style={{ borderLeft: '3px solid var(--mantine-color-orange-5)' }}>
                  <Stack gap={2}>
                    <Text size="sm" fw={500} lineClamp={1}>{d.summary ?? d.subject ?? '(no subject)'}</Text>
                    {deadlines.slice(0, 2).map((dl, i) => (
                      <Text key={i} size="xs" c="orange">⏰ {dl.text}{dl.due_iso ? ` (${dl.due_iso.slice(0, 10)})` : ''}</Text>
                    ))}
                    <Text size="xs" c="dimmed">{d.from_addr} · {formatRelative(d.received_at)}</Text>
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        )}

        {/* Brain-extracted: emails where the user owes the next action. */}
        {((data as never as { ai_user_owes?: Array<{ message_id: string; subject: string | null; from_addr: string; received_at: string; summary: string | null; user_owes: string | null }> }).ai_user_owes ?? []).length > 0 && (
          <Stack gap={4}>
            <Text size="xs" c="blue" tt="uppercase" fw={600}>Pending from you</Text>
            {((data as never as { ai_user_owes: Array<{ message_id: string; subject: string | null; from_addr: string; received_at: string; summary: string | null; user_owes: string | null }> }).ai_user_owes).slice(0, 6).map((d) => (
              <Paper key={d.message_id} p="xs" withBorder
                style={{ borderLeft: '3px solid var(--mantine-color-blue-5)' }}>
                <Stack gap={2}>
                  <Text size="sm" fw={500} lineClamp={1}>{d.summary ?? d.subject ?? '(no subject)'}</Text>
                  {d.user_owes && <Text size="xs" c="blue">→ {d.user_owes}</Text>}
                  <Text size="xs" c="dimmed">{d.from_addr} · {formatRelative(d.received_at)}</Text>
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}

        {/* Risk alerts */}
        {data.risk_alerts.length > 0 && (
          <Stack gap={4}>
            <Text size="xs" c="red" tt="uppercase" fw={600}>Risk alerts</Text>
            {data.risk_alerts.map((a) => (
              <Paper key={a.message_id} p="xs" withBorder
                style={{ borderLeft: '3px solid var(--mantine-color-red-5)' }}>
                <Group gap="xs" wrap="nowrap">
                  <Badge size="xs" color={a.verdict === 'dangerous' ? 'red' : 'orange'}>
                    {a.risk_score}
                  </Badge>
                  <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                    <Text size="sm" fw={500} lineClamp={1}>{a.subject ?? '(no subject)'}</Text>
                    <Text size="xs" c="dimmed" lineClamp={1}>
                      {a.from_addr} · {formatRelative(a.received_at)}
                    </Text>
                  </Stack>
                </Group>
              </Paper>
            ))}
          </Stack>
        )}

        {/* Promises due today — overdue pinned first, then upcoming. */}
        {data.promises_due_today.length > 0 && (() => {
          const now = Date.now();
          const sorted = [...data.promises_due_today].sort((a, b) => {
            const ao = new Date(a.due_at).getTime() < now ? 0 : 1;
            const bo = new Date(b.due_at).getTime() < now ? 0 : 1;
            if (ao !== bo) return ao - bo;
            return new Date(a.due_at).getTime() - new Date(b.due_at).getTime();
          });
          const overdueCount = sorted.filter((p) => new Date(p.due_at).getTime() < now).length;
          return (
            <Stack gap={4}>
              <Group gap={4}>
                <Text size="xs" c={overdueCount > 0 ? 'red' : 'orange'} tt="uppercase" fw={600}>
                  Promises due in 24h
                </Text>
                {overdueCount > 0 && (
                  <Badge size="xs" color="red" variant="filled">{overdueCount} overdue</Badge>
                )}
              </Group>
              {sorted.slice(0, 5).map((p) => {
                const isOverdue = new Date(p.due_at).getTime() < now;
                return (
                  <Paper
                    key={p.id}
                    p="xs"
                    withBorder
                    style={isOverdue ? {
                      borderLeft: '3px solid var(--mantine-color-red-6)',
                      background: 'var(--mantine-color-red-0)',
                    } : undefined}
                  >
                    <Group gap="xs" wrap="nowrap">
                      <Badge size="xs" color={isOverdue ? 'red' : 'orange'}
                        variant={isOverdue ? 'filled' : 'light'}>
                        {isOverdue ? `${formatRelative(p.due_at)} ⚠` : formatRelative(p.due_at)}
                      </Badge>
                      <Text size="sm" lineClamp={2} style={{ flex: 1 }}>{p.promise_text}</Text>
                    </Group>
                  </Paper>
                );
              })}
            </Stack>
          );
        })()}

        {/* Recent decisions */}
        {data.decisions_recent.length > 0 && (
          <Stack gap={4}>
            <Text size="xs" c="teal" tt="uppercase" fw={600}>Recent decisions on file</Text>
            {data.decisions_recent.slice(0, 3).map((d) => (
              <Paper key={d.id} p="xs" withBorder>
                <Text size="sm" lineClamp={2}>{d.decision_text}</Text>
                <Text size="xs" c="dimmed">{formatRelative(d.decided_at)}</Text>
              </Paper>
            ))}
          </Stack>
        )}
      </Stack>
    </Card>
  );
}

// =========================================================================
// Insights — landing page, executive snapshot.
// =========================================================================

export function InsightsPanel({ onSwitchTab }: { onSwitchTab: (t: string) => void }): React.JSX.Element {
  const [stats, setStats] = useState<Awaited<ReturnType<typeof api.addinStats>> | null>(null);
  const [health, setHealth] = useState<Awaited<ReturnType<typeof api.addinHealth>> | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Load health alongside stats
  useEffect(() => {
    api.addinHealth().then(setHealth).catch(() => {});
  }, []);

  function jumpToFiltered(filter: string) {
    sessionStorage.setItem('addin_search_filter', filter);
    onSwitchTab('search');
  }

  async function load() {
    setBusy(true);
    setErr(null);
    try {
      setStats(await api.addinStats());
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not load stats');
    } finally {
      setBusy(false);
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: load on mount only
  useEffect(() => {
    load();
  }, []);

  if (busy && !stats) {
    return (
      <Center p="xl">
        <Loader />
      </Center>
    );
  }
  if (err) {
    return (
      <Alert color="red" variant="light">
        {err}{' '}
        <Button size="xs" onClick={load} ml="sm">
          Retry
        </Button>
      </Alert>
    );
  }
  if (!stats) {
    return (
      <Alert color="yellow" variant="light">
        No data yet. Run an index first.
      </Alert>
    );
  }

  const tierTotal = stats.by_tier_30d.reduce((acc, r) => acc + r.n, 0) || 1;
  const dailyMax = stats.daily_volume_14d.reduce((acc, r) => Math.max(acc, r.n), 0) || 1;

  return (
    <Stack gap="md">
      {/* Phase 9 — Inbox Control Score: replaces the bare A-F card with
          per-bucket breakdown and an actionable improvement path. */}
      {health && <InboxControlScoreCard health={health} onSwitchTab={onSwitchTab} />}

      <SimpleGrid cols={{ base: 2, sm: 5 }} spacing="md">
        <StatCard
          label="Total indexed"
          value={stats.total_indexed}
          accent="blue"
          onClick={() => jumpToFiltered('all')}
        />
        <StatCard
          label="Today"
          value={stats.today}
          accent="cyan"
          subtitle="new emails"
          {...(stats.today > 0 ? { onClick: () => jumpToFiltered('today') } : {})}
        />
        <StatCard
          label="Last 7 days"
          value={stats.last_7d}
          accent="indigo"
          {...(stats.last_7d > 0 ? { onClick: () => jumpToFiltered('7d') } : {})}
        />
        <StatCard
          label="Critical (P0/P1)"
          value={stats.critical_7d}
          accent={stats.critical_7d > 0 ? 'red' : 'gray'}
          subtitle="last 7 days"
          {...(stats.critical_7d > 0 ? { onClick: () => jumpToFiltered('P0') } : {})}
        />
        <StatCard
          label="Last 30 days"
          value={stats.last_30d}
          accent="teal"
          {...(stats.last_30d > 0 ? { onClick: () => jumpToFiltered('30d') } : {})}
        />
      </SimpleGrid>

      <Grid gutter="md">
        <Grid.Col span={{ base: 12, md: 7 }}>
          <Card padding="md" radius="md" withBorder>
            <Group justify="space-between" mb="xs">
              <Title order={5}>Volume — last 14 days</Title>
              <Group gap={4}>
                <Button size="xs" variant="subtle" leftSection={<IconFileText size={12} />}
                  onClick={() => {
                    const esc = (s: string | number | null | undefined) => {
                      if (s == null) return '';
                      const v = String(s);
                      return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
                    };
                    const lines: string[] = [];
                    lines.push('# daily_volume_14d');
                    lines.push('day,count');
                    for (const d of stats.daily_volume_14d) lines.push(`${esc(d.day)},${d.n}`);
                    lines.push('', '# tier_30d');
                    lines.push('tier,count');
                    for (const t of stats.by_tier_30d) lines.push(`${esc(t.tier)},${t.n}`);
                    lines.push('', '# top_senders_7d');
                    lines.push('sender,count');
                    for (const s of stats.top_senders_7d ?? []) lines.push(`${esc(s.from_addr)},${s.n}`);
                    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `insights-${new Date().toISOString().slice(0, 10)}.csv`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    URL.revokeObjectURL(url);
                  }}>
                  CSV
                </Button>
                <Button size="xs" variant="subtle" onClick={load} loading={busy}>
                  Refresh
                </Button>
              </Group>
            </Group>
            {stats.daily_volume_14d.length === 0 ? (
              <Text c="dimmed" size="sm">
                No data yet.
              </Text>
            ) : (
              <Stack gap={4}>
                {stats.daily_volume_14d.map((d) => (
                  <Group key={d.day} wrap="nowrap" gap="sm">
                    <Text size="xs" c="dimmed" w={70} style={{ flexShrink: 0 }}>
                      {d.day}
                    </Text>
                    <Box style={{ flex: 1 }}>
                      <Progress
                        value={(d.n / dailyMax) * 100}
                        size="lg"
                        radius="sm"
                        color="blue"
                      />
                    </Box>
                    <Text size="xs" w={50} ta="right">
                      {d.n.toLocaleString()}
                    </Text>
                  </Group>
                ))}
              </Stack>
            )}
          </Card>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 5 }}>
          <Card padding="md" radius="md" withBorder>
            <Title order={5} mb="xs">
              Tier breakdown — last 30 days
            </Title>
            {stats.by_tier_30d.length === 0 ? (
              <Text c="dimmed" size="sm">
                Nothing classified yet.
              </Text>
            ) : (
              <Stack gap={6}>
                {stats.by_tier_30d.map((t) => (
                  <Group key={t.tier} wrap="nowrap" gap="sm">
                    <Badge color={tierColor(t.tier)} w={110} style={{ flexShrink: 0 }}>
                      {t.tier}
                    </Badge>
                    <Box style={{ flex: 1 }}>
                      <Progress
                        value={(t.n / tierTotal) * 100}
                        color={tierColor(t.tier)}
                        size="md"
                      />
                    </Box>
                    <Text size="sm" fw={500} w={50} ta="right">
                      {t.n.toLocaleString()}
                    </Text>
                  </Group>
                ))}
              </Stack>
            )}
          </Card>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 6 }}>
          <Card padding="md" radius="md" withBorder>
            <Title order={5} mb="xs">
              Top senders — last 7 days
            </Title>
            {stats.top_senders_7d.length === 0 ? (
              <Text c="dimmed" size="sm">
                No senders yet.
              </Text>
            ) : (
              <Stack gap={4}>
                {stats.top_senders_7d.map((s) => (
                  <Group key={s.from_addr} justify="space-between" wrap="nowrap">
                    <Text size="sm" lineClamp={1}>
                      {s.from_addr}
                    </Text>
                    <Group gap="xs">
                      <Text size="xs" c="dimmed">
                        {formatRelative(s.last_received)}
                      </Text>
                      <Badge variant="light">{s.n}</Badge>
                    </Group>
                  </Group>
                ))}
              </Stack>
            )}
          </Card>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 6 }}>
          <Card padding="md" radius="md" withBorder>
            <Title order={5} mb="xs">
              Folders indexed
            </Title>
            {stats.by_folder.length === 0 ? (
              <Text c="dimmed" size="sm">
                Nothing indexed yet — open Index tab to start.
              </Text>
            ) : (
              <Stack gap={4}>
                {stats.by_folder.slice(0, 8).map((f) => (
                  <Group key={f.folder} justify="space-between" wrap="nowrap">
                    <Text size="sm" lineClamp={1}>
                      {f.folder}
                    </Text>
                    <Group gap="xs">
                      <Text size="xs" c="dimmed">
                        {f.last_received ? formatRelative(f.last_received) : 'never'}
                      </Text>
                      <Badge variant="light">{f.n.toLocaleString()}</Badge>
                    </Group>
                  </Group>
                ))}
              </Stack>
            )}
          </Card>
        </Grid.Col>
      </Grid>
    </Stack>
  );
}

export function StatCard({
  label,
  value,
  accent,
  subtitle,
  onClick,
}: {
  label: string;
  value: number;
  accent: string;
  subtitle?: string;
  onClick?: () => void;
}): React.JSX.Element {
  return (
    <Card
      padding="md"
      radius="md"
      withBorder
      style={{
        cursor: onClick ? 'pointer' : undefined,
        borderLeft: `4px solid var(--mantine-color-${accent}-6)`,
      }}
      onClick={onClick}
    >
      <Stack gap={2}>
        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
          {label}
        </Text>
        <Text fw={700} size="xl">
          {value.toLocaleString()}
        </Text>
        {subtitle && (
          <Text size="xs" c="dimmed">
            {subtitle}
          </Text>
        )}
      </Stack>
    </Card>
  );
}

// =========================================================================
// Search — smart filters + instant results + inline preview.
// =========================================================================


export function BriefPanel(): React.JSX.Element {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.addinMorningNarrative>> | null>(null);
  const [busy, setBusy] = useState(false);
  // Per-thread inline edit state for AI drafts.
  const [draftEdits, setDraftEdits] = useState<Record<string, string>>({});
  const [editingThread, setEditingThread] = useState<string | null>(null);
  // Optimistic-hide set: once the user takes action on a thread, fade it
  // out immediately rather than waiting for the next refetch.
  const [hiddenThreads, setHiddenThreads] = useState<Set<string>>(new Set());

  async function load(): Promise<void> {
    setBusy(true);
    try {
      const r = await api.addinMorningNarrative();
      setData(r);
      setHiddenThreads(new Set()); // reset optimistic hides on fresh load
    } catch (e) {
      notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Brief failed to load' });
    } finally {
      setBusy(false);
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: load on mount only
  useEffect(() => { load(); }, []);

  if (busy && !data) {
    // Round 28-AC follow-up: time-of-day-aware loader label. Saying
    // "morning brief" at 10pm reads as broken UX (the user actually
    // flagged this). Use the same buckets as MorningBriefBanner.
    const hour = new Date().getHours();
    const slot =
      hour < 5 ? 'late-night' :
      hour < 11 ? 'morning' :
      hour < 17 ? 'midday' :
      hour < 22 ? 'evening' : 'late-night';
    return (
      <Center p="xl">
        <Stack align="center">
          <Loader />
          <Text size="sm" c="dimmed">
            Building your {slot} brief…
          </Text>
        </Stack>
      </Center>
    );
  }
  if (!data) {
    return (
      <Card padding="md" withBorder>
        <Text>No brief available. Try again in a moment.</Text>
        <Button size="xs" mt="xs" onClick={load}>Refresh</Button>
      </Card>
    );
  }

  function inWebView2(): boolean {
    return !!(window as unknown as { chrome?: { webview?: unknown } }).chrome?.webview;
  }

  function openInOutlook(messageId: string, label: string | null): void {
    notifications.show({
      color: inWebView2() ? 'blue' : 'orange',
      title: inWebView2() ? 'Asking Outlook to open…' : 'Not in addin',
      message: label ?? '(no subject)',
      autoClose: 2000,
    });
    postToHost({ type: 'open_in_outlook', message_id: messageId });
  }

  function approveDraft(threadKey: string, messageId: string, body: string, subject: string | null, fromAddr: string): void {
    if (!body.trim()) {
      notifications.show({ color: 'red', message: 'Draft is empty' });
      return;
    }
    if (!inWebView2()) {
      notifications.show({ color: 'orange', message: 'Drafts can only be saved from inside Outlook' });
      return;
    }
    postToHost({
      type: 'send_as_reply',
      message_id: messageId,
      to: fromAddr,
      subject: (subject ?? '').startsWith('Re:') ? subject : `Re: ${subject ?? ''}`,
      body,
    });
    notifications.show({ color: 'green', message: 'Saved to your Outlook Drafts' });
    // Optimistic hide — thread acted on
    setHiddenThreads((prev) => { const n = new Set(prev); n.add(threadKey); return n; });
    setEditingThread(null);
  }

  async function snoozeThread(threadKey: string, messageId: string, days: number): Promise<void> {
    setHiddenThreads((prev) => { const n = new Set(prev); n.add(threadKey); return n; });
    try {
      await api.addinActionInboxBulk({ message_ids: [messageId], action: 'snooze_30d' });
      notifications.show({ color: 'blue', message: `Thread snoozed (${days}d)` });
    } catch (e) {
      // Restore on failure
      setHiddenThreads((prev) => { const n = new Set(prev); n.delete(threadKey); return n; });
      notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Snooze failed' });
    }
  }

  async function acknowledgeThread(threadKey: string, messageId: string): Promise<void> {
    setHiddenThreads((prev) => { const n = new Set(prev); n.add(threadKey); return n; });
    try {
      await api.addinActionInboxBulk({ message_ids: [messageId], action: 'acknowledge' });
      notifications.show({ color: 'green', message: 'Acknowledged — won\'t show again' });
    } catch (e) {
      setHiddenThreads((prev) => { const n = new Set(prev); n.delete(threadKey); return n; });
      notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Acknowledge failed' });
    }
  }

  // Render the LLM-generated narrative as paragraphs/bullets. The model
  // emits text with newlines + "• " bullet lines; preserve them visually.
  function renderNarrative(text: string): React.JSX.Element {
    const lines = text.split('\n').filter((l) => l.trim().length > 0);
    return (
      <Stack gap={6}>
        {lines.map((line, i) => {
          const trimmed = line.trim();
          if (trimmed.startsWith('•') || trimmed.startsWith('-')) {
            return <Text key={i} size="sm" pl={16}>{trimmed.replace(/^[•-]\s*/, '• ')}</Text>;
          }
          if (trimmed.toLowerCase().startsWith('most urgent:')) {
            return <Text key={i} size="sm" fw={600} c="red" mt={4}>{trimmed}</Text>;
          }
          return <Text key={i} size="sm">{trimmed}</Text>;
        })}
      </Stack>
    );
  }

  const visibleThreads = data.active_threads.filter(
    (t) => !hiddenThreads.has(t.thread_id ?? `mid:${t.message_id}`),
  );
  const hiddenCount = data.active_threads.length - visibleThreads.length;

  // Self-celebration hero banner. Fires only on the user's own birthday
  // or work-anniversary (exact day, HRPartner-sourced). Renders ABOVE the
  // greeting card so it's the first thing the user sees on the day.
  const selfHero = (() => {
    const sm = data.self_milestone;
    if (!sm) return null;
    if (sm.kind === 'birthday') {
      return {
        emoji: '🎂',
        gradient: 'linear-gradient(135deg, var(--mantine-color-pink-1), var(--mantine-color-violet-1))',
        accent: 'var(--mantine-color-pink-7)',
        title: `Happy birthday, ${sm.display_name}!`,
        subtitle: 'Have a wonderful day.',
      };
    }
    const years = sm.years ?? 1;
    const roundCallout = sm.is_round ? ' — milestone!' : '';
    return {
      emoji: '🎉',
      gradient: 'linear-gradient(135deg, var(--mantine-color-yellow-1), var(--mantine-color-teal-1))',
      accent: 'var(--mantine-color-teal-7)',
      title: `${years} year${years === 1 ? '' : 's'} at BroadNet today, ${sm.display_name}${roundCallout}`,
      subtitle: years >= 5
        ? "Thank you for everything you've built here."
        : 'Thanks for being part of the team.',
    };
  })();

  return (
    <Stack gap="md">
      {selfHero && (
        <Card padding="lg" radius="md" withBorder style={{ background: selfHero.gradient }}>
          <Group gap="md" align="center">
            <Text style={{ fontSize: 40, lineHeight: 1 }}>{selfHero.emoji}</Text>
            <Stack gap={4}>
              <Title order={3} style={{ color: selfHero.accent }}>{selfHero.title}</Title>
              <Text size="sm" c="dark.7">{selfHero.subtitle}</Text>
            </Stack>
          </Group>
        </Card>
      )}

      {/* Greeting + decision count */}
      <Card padding="md" radius="md" withBorder>
        <Group justify="space-between" align="flex-start">
          <Stack gap={2}>
            <Title order={3}>{data.greeting}</Title>
            <Text size="sm" c={data.decisions_count > 0 ? 'red' : 'dimmed'}>
              {data.decisions_count > 0
                ? `${data.decisions_count} thread${data.decisions_count === 1 ? '' : 's'} need your decision today`
                : 'Inbox zero on decisions. ✓'}
            </Text>
          </Stack>
          <Button size="xs" variant="default" onClick={load} loading={busy}>Refresh</Button>
        </Group>
      </Card>

      {/* Narrative paragraph (LLM-generated chief-of-staff summary) */}
      {data.narrative && (
        <Card padding="md" radius="md" withBorder>
          <Group gap="xs" mb="xs">
            <IconNews size={18} color="var(--mantine-color-blue-6)" />
            <Title order={5}>Today's narrative</Title>
          </Group>
          {renderNarrative(data.narrative)}
        </Card>
      )}

      {/* Active threads — the actual decisions */}
      {visibleThreads.length > 0 && (
        <Card padding="md" radius="md" withBorder>
          <Group gap="xs" mb="md">
            <IconBolt size={18} color="var(--mantine-color-red-6)" />
            <Title order={5}>Active threads — decisions you owe</Title>
            {hiddenCount > 0 && (
              <Badge color="green" variant="light" size="sm">{hiddenCount} handled</Badge>
            )}
          </Group>
          <Stack gap="md">
            {visibleThreads.map((t) => {
              const threadKey = t.thread_id ?? `mid:${t.message_id}`;
              const ageHrs = Math.floor((Date.now() - new Date(t.latest_received_at).getTime()) / 3_600_000);
              const ageStr = ageHrs < 1 ? '<1h ago' : ageHrs < 24 ? `${ageHrs}h ago` : `${Math.floor(ageHrs / 24)}d ago`;
              const isEditing = editingThread === threadKey;
              const draftValue = isEditing
                ? draftEdits[threadKey] ?? t.ai_draft ?? ''
                : t.ai_draft ?? '';

              return (
                <Card key={threadKey} padding="sm" radius="sm" withBorder
                  style={{ borderLeft: `3px solid var(--mantine-color-${t.tier === 'P0' ? 'red' : t.tier === 'P1' ? 'orange' : 'blue'}-6)` }}>
                  <Stack gap="xs">
                    {/* Header */}
                    <Group justify="space-between" wrap="nowrap">
                      <Group gap={6} style={{ flex: 1, minWidth: 0 }}>
                        {t.tier && <Badge color={tierColor(t.tier)} size="sm">{t.tier}</Badge>}
                        <Text fw={600} size="sm" lineClamp={1} style={{ flex: 1, minWidth: 0 }}>
                          {t.subject ?? '(no subject)'}
                        </Text>
                      </Group>
                      <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
                        {t.from_addr} · {ageStr}
                        {t.message_count > 1 && ` · ${t.message_count} msgs`}
                      </Text>
                    </Group>

                    {/* AI draft block */}
                    {t.ai_draft ? (
                      <Card padding="xs" radius="sm" bg="var(--mantine-color-blue-0)"
                        style={{ borderColor: 'var(--mantine-color-blue-3)' }} withBorder>
                        <Group gap={4} mb={4}>
                          <IconSparkles size={12} color="var(--mantine-color-blue-7)" />
                          <Text size="xs" fw={600} c="blue.7">AI suggests reply</Text>
                        </Group>
                        {isEditing ? (
                          <Textarea
                            value={draftValue}
                            onChange={(e) => setDraftEdits((prev) => ({ ...prev, [threadKey]: e.currentTarget.value }))}
                            autosize
                            minRows={3}
                            maxRows={10}
                            size="sm"
                          />
                        ) : (
                          <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{t.ai_draft}</Text>
                        )}
                      </Card>
                    ) : (
                      <Text size="xs" c="dimmed" fs="italic">No AI draft available — open thread to reply manually.</Text>
                    )}

                    {/* Action row */}
                    <Group gap="xs" wrap="wrap">
                      {t.ai_draft && !isEditing && (
                        <Button size="xs" leftSection={<IconCheck size={12} />} color="green"
                          onClick={() => approveDraft(threadKey, t.message_id, t.ai_draft ?? '', t.subject, t.from_addr)}>
                          Approve & save draft
                        </Button>
                      )}
                      {t.ai_draft && !isEditing && (
                        <Button size="xs" variant="subtle" leftSection={<IconEdit size={12} />}
                          onClick={() => setEditingThread(threadKey)}>
                          Edit
                        </Button>
                      )}
                      {isEditing && (
                        <>
                          <Button size="xs" leftSection={<IconCheck size={12} />} color="green"
                            onClick={() => approveDraft(threadKey, t.message_id, draftValue, t.subject, t.from_addr)}>
                            Save edited draft
                          </Button>
                          <Button size="xs" variant="subtle" onClick={() => setEditingThread(null)}>
                            Cancel
                          </Button>
                        </>
                      )}
                      <Button size="xs" variant="subtle" leftSection={<IconMail size={12} />}
                        onClick={() => openInOutlook(t.message_id, t.subject)}>
                        Open thread
                      </Button>
                      <Button size="xs" variant="subtle" leftSection={<IconClock size={12} />}
                        onClick={() => snoozeThread(threadKey, t.message_id, 30)}>
                        Snooze 30d
                      </Button>
                      <Button size="xs" variant="subtle" leftSection={<IconArchive size={12} />} color="gray"
                        onClick={() => acknowledgeThread(threadKey, t.message_id)}>
                        Acknowledge
                      </Button>
                    </Group>
                  </Stack>
                </Card>
              );
            })}
          </Stack>
        </Card>
      )}

      {visibleThreads.length === 0 && data.active_threads.length === 0 && (
        <Card padding="md" radius="md" withBorder>
          <Text c="dimmed" ta="center">No active threads need your decision. ✓</Text>
        </Card>
      )}

      {/* Risks */}
      {data.risks.length > 0 && (
        <Card padding="md" radius="md" withBorder>
          <Group gap="xs" mb="xs">
            <IconShield size={18} color="var(--mantine-color-red-6)" />
            <Title order={5}>Risks worth a glance</Title>
            <Badge color="red" variant="light" size="sm">{data.risks.length}</Badge>
          </Group>
          <Stack gap={6}>
            {data.risks.map((r) => (
              <Group key={r.message_id} gap="sm" wrap="nowrap"
                onClick={() => openInOutlook(r.message_id, r.subject)}
                style={{ cursor: 'pointer' }}>
                <Badge color={r.verdict === 'dangerous' ? 'red' : 'yellow'} variant="filled" size="sm">
                  {r.verdict}
                </Badge>
                <Text size="sm" lineClamp={1} style={{ flex: 1, minWidth: 0 }}>
                  {r.subject ?? '(no subject)'}
                </Text>
                <Text size="xs" c="dimmed">{r.from_addr}</Text>
              </Group>
            ))}
          </Stack>
        </Card>
      )}

      {/* Team milestones — birthdays + tenure (May 2026, HRPartner-backed) */}
      {data.team_milestones &&
        (data.team_milestones.birthdays.length > 0 ||
          data.team_milestones.tenure.length > 0) && (
        <Card padding="md" radius="md" withBorder bg="var(--mantine-color-pink-0)">
          <Group gap="xs" mb="xs">
            <IconUsersGroup size={18} color="var(--mantine-color-pink-7)" />
            <Title order={5}>Team today</Title>
          </Group>
          <Stack gap={6}>
            {data.team_milestones.birthdays.map((b) => {
              // Round 36 follow-up: show FULL name (with family name) rather
              // than just preferred_name. User feedback: "I need the name with
              // family name and in which branch". Fall back through full_name
              // → preferred_name → email so we never render nothing.
              const name = b.full_name ?? b.preferred_name ?? b.email;
              const role = b.position
                ? ` · ${b.position}${b.department && b.department !== b.position ? `, ${b.department}` : ''}${b.location ? `, ${b.location}` : ''}`
                : (b.location ? ` · ${b.location}` : '');
              const whenLabel =
                b.when === 'today' ? '' :
                b.when === 'tomorrow' ? ' (tomorrow)' : ' (yesterday)';
              return (
                <Text key={`bd-${b.email}`} size="sm">
                  🎂 <b>{name}</b>'s birthday{whenLabel}
                  <Text component="span" size="sm" c="dimmed">{role}</Text>
                </Text>
              );
            })}
            {data.team_milestones.tenure.map((t) => {
              const name = t.full_name ?? t.preferred_name ?? t.email;
              const role = t.position
                ? ` · ${t.position}${t.department && t.department !== t.position ? `, ${t.department}` : ''}${t.location ? `, ${t.location}` : ''}`
                : (t.location ? ` · ${t.location}` : '');
              const whenLabel = t.when === 'today' ? '' : ' (this week)';
              const milestoneLabel = t.is_round
                ? `${t.years}-year milestone`
                : `${t.years}-year anniversary`;
              return (
                <Text key={`tn-${t.email}`} size="sm">
                  🎉 <b>{name}</b>'s {milestoneLabel}{whenLabel}
                  <Text component="span" size="sm" c="dimmed">{role}</Text>
                </Text>
              );
            })}
          </Stack>
        </Card>
      )}

      {/* AI did overnight */}
      <Card padding="md" radius="md" withBorder bg="var(--mantine-color-gray-0)">
        <Group gap="xs" mb="xs">
          <IconSparkles size={18} color="var(--mantine-color-violet-6)" />
          <Title order={5}>While you slept, AI handled</Title>
        </Group>
        <Stack gap={4}>
          <Text size="sm">
            • Classified <b>{data.ai_overnight.classified}</b> emails
            ({data.ai_overnight.newsletters_handled} newsletters auto-archived)
          </Text>
          {data.ai_overnight.drafts_generated > 0 && (
            <Text size="sm">• Drafted <b>{data.ai_overnight.drafts_generated}</b> replies (above)</Text>
          )}
          {data.ai_overnight.promises_detected > 0 && (
            <Text size="sm">
              • Detected <b>{data.ai_overnight.promises_detected}</b> commitment{data.ai_overnight.promises_detected === 1 ? '' : 's'} from your sent mail
            </Text>
          )}
          {data.ai_overnight.risks_flagged > 0 && (
            <Text size="sm">
              • Flagged <b>{data.ai_overnight.risks_flagged}</b> risk{data.ai_overnight.risks_flagged === 1 ? '' : 's'}
            </Text>
          )}
        </Stack>
      </Card>

      <Text size="xs" c="dimmed" ta="center">
        Generated {shortDate(data.generated_at)} · cached 30 min · refresh to recompute
      </Text>
    </Stack>
  );
}

// =========================================================================
// Round 28-L Phase 1.2b — user-tracked followups CRUD section.
// Lives above the auto-detected "awaiting reply" list inside FollowupsPanel.
// =========================================================================

